import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import * as eventApis from 'apis/events';
import { YoutubeVideoPlayer } from '../YoutubeVideoPlayer';
import { getBookingState, isEventPast } from '../EventListStatic/eventState';
import './EventDetailsStatic.scss';

/*
  Event Details layout, converted from the live-test.html mockup.
  Self-contained component:
  - All CSS is scoped under ".ed-page" with "ed-" prefixed classes so it
    can be dropped into the Dashboard without colliding with the app's
    global styles.
  - Renders real event data from the API. Empty fields/sections are
    omitted rather than falling back to the mockup's placeholder copy.

  Section order puts the concrete "what do I get" content first and the
  fine print last: hero, what's included + gallery, about, point of
  contact, video, more upcoming events, membership CTA, terms. The two
  long copy blocks (about and terms) collapse to a few lines behind a
  fade - see CollapsibleCopy.

  Field mapping (taurian-backend Event entity):
    firstName / lastName  -> hero title, lastName rendered as the red
                               accent line
    tagline               -> line under the title
    shortDescription      -> hero teaser
    description           -> "About the event" body
    organizerName /
      organizerPhone /
      organizerEmail      -> "Point of contact" after About
    features              -> "What's included" checklist
    headerImage           -> hero background (falls back to images[0])
    images                -> gallery
    eventDate             -> date + time meta
    location              -> location meta
    entryFee              -> rupee meta, external events only
    participantCount /
      maxParticipants     -> "slots left" chip
    type / status /
      isRegistered /
      isWaitlisted        -> which hero action is shown
*/

const UPCOMING_LIMIT = 3;

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const toDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDate = (date) =>
  date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

const formatWeekday = (date) => date.toLocaleDateString('en-GB', { weekday: 'long' });

const formatTime = (date) =>
  date.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });

// Decimal columns arrive as strings. Empty and non-numeric values stay hidden.
const formatEntryFee = (value) => {
  if (value == null || value === '') return null;
  const amount = Number(value);
  if (!Number.isFinite(amount)) return null;
  return amount.toLocaleString('en-IN', { maximumFractionDigits: 2 });
};

// The entity has no end date, so calendar entries get a 2h block starting
// at eventDate.
const CALENDAR_DURATION_MS = 2 * 60 * 60 * 1000;

const toCalendarStamp = (date) => date.toISOString().replace(/[-:]|\.\d{3}/g, '');

const buildCalendarUrl = (event, date) => {
  const end = new Date(date.getTime() + CALENDAR_DURATION_MS);
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: [event.firstName, event.lastName].filter(Boolean).join(' '),
    dates: `${toCalendarStamp(date)}/${toCalendarStamp(end)}`,
  });
  if (event.shortDescription) params.set('details', event.shortDescription);
  if (event.location) params.set('location', event.location);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const getYouTubeVideoId = (url) => {
  if (!url) return null;
  const regex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  const match = url.match(regex);
  return match ? match[1] : null;
};

/*
  Clamps long copy to the height set by --collapsed-height in the
  stylesheet and expands it on the toggle underneath.

  The cap is read back out of CSS instead of being a number here so the
  responsive overrides stay in the stylesheet, and the expanded height is
  measured from the content rather than animated to a guess, so a late
  font or a reflow cannot leave the block clipped. Copy that already fits
  renders as plain paragraphs with no toggle at all.
*/
const CollapsibleCopy = ({ id, moreLabel, lessLabel, children }) => {
  const bodyRef = useRef(null);
  const contentRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [contentHeight, setContentHeight] = useState(null);
  const [collapsedHeight, setCollapsedHeight] = useState(null);
  // Held back until after the first paint: the measurement below settles
  // the block's height on mount, which would otherwise animate.
  const [animate, setAnimate] = useState(false);

  useEffect(() => setAnimate(true), []);

  useLayoutEffect(() => {
    const body = bodyRef.current;
    const content = contentRef.current;
    if (!body || !content) return undefined;

    const measure = () => {
      // Rounded up off the fractional box height: scrollHeight truncates,
      // which can shave the descenders off the last line once expanded.
      setContentHeight(Math.ceil(content.getBoundingClientRect().height));
      setCollapsedHeight(
        parseFloat(window.getComputedStyle(body).getPropertyValue('--collapsed-height')),
      );
    };

    measure();

    // The observer catches the content growing; the resize listener catches
    // the cap itself changing at a breakpoint.
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    if (observer) observer.observe(content);
    window.addEventListener('resize', measure);

    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  const needsToggle =
    contentHeight !== null &&
    Number.isFinite(collapsedHeight) &&
    contentHeight > collapsedHeight + 4;

  const expanded = open || !needsToggle;

  return (
    <div className="ed-collapse">

      <div
        id={id}
        ref={bodyRef}
        className={`ed-collapse-body ${expanded ? 'ed-is-open' : ''} ${
          animate ? 'ed-animate' : ''
        }`}
        style={
          expanded && contentHeight !== null ? { maxHeight: `${contentHeight}px` } : undefined
        }
      >
        <div ref={contentRef}>{children}</div>
      </div>

      {needsToggle && (
        <button
          type="button"
          className={`ed-collapse-toggle ${open ? 'ed-is-open' : ''}`}
          onClick={() => setOpen((value) => !value)}
          aria-controls={id}
          aria-expanded={open}
        >
          <span>{open ? lessLabel : moreLabel}</span>
          <i className="fa-solid fa-chevron-down" />
        </button>
      )}

    </div>
  );
};

const EventDetailsStatic = () => {
  const { id } = useParams();
  const assetUrl = process.env.REACT_APP_TAURIAN_ASSET_URL;

  const [event, setEvent] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState(null);

  // The dashboard list is the only endpoint that carries the member's own
  // registration state, so the event is picked out of it by id.
  const loadEvent = useCallback(async () => {
    try {
      const response = await eventApis.fetchUserEvents();
      const found = (response.data || []).find((item) => String(item.id) === String(id));
      setEvent(found || null);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    setMessage(null);
    loadEvent();
  }, [loadEvent]);

  useEffect(() => {
    if (!id) return;

    const fetchUpcoming = async () => {
      try {
        const response = await eventApis.fetchUpcomingEvents({
          exclude: id,
          limit: UPCOMING_LIMIT,
        });
        setUpcoming(response.data || []);
      } catch (error) {
        console.log(error);
        setUpcoming([]);
      }
    };

    fetchUpcoming();
  }, [id]);

  const handleRegister = async () => {
    setActionLoading(true);
    setMessage(null);
    try {
      const response = await eventApis.registerForEvent(id);
      await loadEvent();
      // The backend waitlists the registration instead of rejecting it once
      // the event is full, so the message follows the returned status.
      setMessage({
        type: 'success',
        text:
          response?.data?.status === 'waiting'
            ? 'The event is full - you have been added to the waiting list.'
            : 'Registration successful.',
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error?.response?.data?.message || 'Registration failed.',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    setActionLoading(true);
    setMessage(null);
    try {
      await eventApis.cancelRegistration(id);
      await loadEvent();
      setMessage({ type: 'success', text: 'Registration cancelled.' });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error?.response?.data?.message || 'Cancellation failed.',
      });
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="ed-page">
        <div className="ed-loading">Loading event...</div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="ed-page">
        <div className="ed-container ed-back-bar">
          <Link className="ed-back-link" to="/dashboard/events">
            <i className="fa-solid fa-arrow-left" />Back to Events
          </Link>
        </div>
        <div className="ed-loading">This event is no longer available.</div>
      </div>
    );
  }

  const assetSrc = (key) => (key ? `${assetUrl}${key}` : '');

  const heroImage = assetSrc(event.headerImage || event.images?.[0] || event.image);
  const galleryImages = (event.images || []).map(assetSrc).filter(Boolean);
  const features = event.features || [];
  // Decorative backdrop for the membership CTA - the last gallery image is
  // preferred over the header image so it is not the hero photo twice.
  const ctaImage = galleryImages[galleryImages.length - 1] || heroImage;
  const eventDate = toDate(event.eventDate);
  const entryFeeLabel =
    event.type === 'external' ? formatEntryFee(event.entryFee) : null;
  const videoId = getYouTubeVideoId(event.youtubeLink);

  const isPast = isEventPast(event);
  const bookingState = getBookingState(event);
  const openDate = toDate(event.registrationOpenDate);

  // Slots only matter while a booking can still be made.
  const showSlots =
    event.maxParticipants != null &&
    (bookingState === 'open' || bookingState === 'not-open');
  const slotsLeft = showSlots
    ? Math.max(event.maxParticipants - (event.participantCount || 0), 0)
    : null;

  const endedNote = (
    <div className="ed-hero-note ed-muted">
      <i className="fa-solid fa-flag-checkered" />
      This event has ended.
    </div>
  );

  const calendarAction = eventDate && !isPast ? (
    <a
      className="ed-btn ed-btn-outline"
      href={buildCalendarUrl(event, eventDate)}
      target="_blank"
      rel="noopener noreferrer"
    >
      <i className="fa-regular fa-calendar-plus" />Add to Calendar
    </a>
  ) : null;

  const renderActions = () => {
    if (event.type === 'external') {
      return (
        <>
          {(event.externalLink || calendarAction) && (
            <div className="ed-hero-actions">
              {event.externalLink && (
                <a
                  className="ed-btn ed-btn-primary"
                  href={event.externalLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fa-solid fa-arrow-up-right-from-square" />Visit Event
                </a>
              )}
              {calendarAction}
            </div>
          )}
          {isPast && endedNote}
        </>
      );
    }

    if (event.type === 'standard') {
      if (isPast) return endedNote;

      return (
        <>
          {calendarAction && <div className="ed-hero-actions">{calendarAction}</div>}
          <div className="ed-hero-note ed-muted">
            <i className="fa-solid fa-circle-info" />
            {event.status === 'completed'
              ? 'The draw for this event has been completed.'
              : "Active members are automatically entered into this event's draw."}
          </div>
        </>
      );
    }

    if (isPast) {
      if (!event.isRegistered) return endedNote;

      return (
        <div className="ed-hero-note ed-ok">
          <i className="fa-solid fa-circle-check" />
          This event has ended. You attended this event.
        </div>
      );
    }

    if (event.isRegistered || event.isWaitlisted) {
      // The backend refuses cancellation once the draw has run.
      const canCancel = event.status !== 'completed';

      return (
        <>
          {(canCancel || calendarAction) && (
            <div className="ed-hero-actions">
              {canCancel && (
                <button
                  type="button"
                  className="ed-btn ed-btn-outline"
                  onClick={handleCancel}
                  disabled={actionLoading}
                >
                  {actionLoading
                    ? 'Please wait...'
                    : event.isWaitlisted
                      ? 'Leave Waitlist'
                      : 'Cancel Registration'}
                </button>
              )}
              {calendarAction}
            </div>
          )}
          <div className={`ed-hero-note ${event.isWaitlisted ? 'ed-warn' : 'ed-ok'}`}>
            <i className="fa-solid fa-circle-check" />
            {event.isWaitlisted
              ? 'You are on the waiting list for this event.'
              : 'You are registered for this event.'}
          </div>
        </>
      );
    }

    if (bookingState === 'not-open') {
      return (
        <>
          {calendarAction && <div className="ed-hero-actions">{calendarAction}</div>}
          <div className="ed-hero-note ed-muted">
            <i className="fa-solid fa-clock" />
            {openDate
              ? `Booking opens on ${formatDate(openDate)}, ${formatTime(openDate)} IST.`
              : 'Booking has not opened yet.'}
          </div>
        </>
      );
    }

    if (bookingState === 'closed') {
      return (
        <>
          {calendarAction && <div className="ed-hero-actions">{calendarAction}</div>}
          <div className="ed-hero-note ed-muted">
            <i className="fa-solid fa-lock" />
            Registration is closed for this event.
          </div>
        </>
      );
    }

    return (
      <div className="ed-hero-actions">
        <button
          type="button"
          className="ed-btn ed-btn-primary"
          onClick={handleRegister}
          disabled={actionLoading}
        >
          <i className="fa-solid fa-flag-checkered" />
          {actionLoading
            ? 'Please wait...'
            : slotsLeft === 0
              ? 'Join Waitlist'
              : 'Book Your Spot'}
        </button>
        {calendarAction}
      </div>
    );
  };

  return (
    <div className="ed-page">

      {/* ===== BACK LINK ===== */}
      <div className="ed-container ed-back-bar">
        <Link className="ed-back-link" to="/dashboard/events">
          <i className="fa-solid fa-arrow-left" />Back to Events
        </Link>
      </div>

      {/* ===== HERO ===== */}
      <section className="ed-hero">

        {heroImage && (
          <div className="ed-hero-bg">
            <img src={heroImage} alt="" />
          </div>
        )}

        {slotsLeft !== null && (
          <div className="ed-hero-chip">
            <i className="fa-solid fa-user-group" />
            <div>
              <span className="ed-hero-chip-label">Limited slots</span>
              <span className="ed-hero-chip-value">
                {slotsLeft} / {event.maxParticipants} slots left
              </span>
            </div>
          </div>
        )}

        <div className="ed-hero-inner">
          <div className="ed-container">
            <div className="ed-hero-content">

              <span className="ed-hero-eyebrow">Featured event</span>

              <h1 className="ed-hero-title">
                {event.firstName}
                {event.lastName && <span className="ed-accent">{event.lastName}</span>}
              </h1>

              {event.tagline && <p className="ed-hero-tagline">{event.tagline}</p>}

              {event.shortDescription && (
                <p className="ed-hero-lede">{event.shortDescription}</p>
              )}

              {(eventDate || event.location || entryFeeLabel) && (
                <div className="ed-hero-meta">

                  {eventDate && (
                    <div className="ed-meta-item">
                      <i className="fa-solid fa-calendar-days" />
                      <div>
                        <span className="ed-meta-title">{formatDate(eventDate)}</span>
                        <span className="ed-meta-sub">{formatWeekday(eventDate)}</span>
                      </div>
                    </div>
                  )}

                  {eventDate && (
                    <div className="ed-meta-item">
                      <i className="fa-solid fa-clock" />
                      <div>
                        <span className="ed-meta-title">{formatTime(eventDate)}</span>
                        <span className="ed-meta-sub">IST</span>
                      </div>
                    </div>
                  )}

                  {event.location && (
                    <div className="ed-meta-item">
                      <i className="fa-solid fa-location-dot" />
                      <div>
                        <span className="ed-meta-title">{event.location}</span>
                      </div>
                    </div>
                  )}

                  {entryFeeLabel && (
                    <div className="ed-meta-item">
                      <i className="fa-solid fa-indian-rupee-sign" />
                      <div>
                        <span className="ed-meta-title">{entryFeeLabel}</span>
                        <span className="ed-meta-sub">Entry fee</span>
                      </div>
                    </div>
                  )}

                </div>
              )}

              {renderActions()}

              {message && (
                <div className={`ed-hero-note ${message.type === 'success' ? 'ed-ok' : 'ed-error'}`}>
                  <i className={`fa-solid ${message.type === 'success' ? 'fa-circle-check' : 'fa-circle-exclamation'}`} />
                  {message.text}
                </div>
              )}

              <div className="ed-hero-badge">
                <i className="fa-solid fa-crown" />Exclusive for Members
              </div>

            </div>
          </div>
        </div>

      </section>

      {/* ===== WHAT'S INCLUDED + GALLERY ===== */}
      {(features.length > 0 || galleryImages.length > 0) && (
        <section className="ed-section">
          <div className="ed-container">
            <div
              className={`ed-included-row ${
                features.length === 0 || galleryImages.length === 0 ? 'ed-single' : ''
              }`}
            >

              {features.length > 0 && (
                <div>
                  <div className="ed-section-head">
                    <h2 className="ed-section-title">What&apos;s included</h2>
                  </div>
                  <ul className="ed-included-list">
                    {features.map((feature, index) => (
                      <li key={`${feature}-${index}`}>
                        <i className="fa-solid fa-circle-check" />{feature}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {galleryImages.length > 0 && (
                <div className="ed-gallery">
                  {galleryImages.map((src, index) => (
                    <figure key={src}>
                      <img src={src} alt={`${event.firstName} ${index + 1}`} />
                    </figure>
                  ))}
                </div>
              )}

            </div>
          </div>
        </section>
      )}

      {/* ===== ABOUT ===== */}
      {event.description && (
        <section className="ed-section">
          <div className="ed-container">
            <div className="ed-about-copy">

              <div className="ed-section-head">
                <h2 className="ed-section-title">About the event</h2>
              </div>

              <CollapsibleCopy id="ed-about-body" moreLabel="Read more" lessLabel="Show less">
                <p>{event.description}</p>
              </CollapsibleCopy>

            </div>
          </div>
        </section>
      )}

      {/* ===== POINT OF CONTACT ===== */}
      {(event.organizerName || event.organizerPhone || event.organizerEmail) && (
        <section className="ed-section">
          <div className="ed-container">
            <div className="ed-section-head">
              <h2 className="ed-section-title">Point of contact</h2>
            </div>
            <div className="ed-contact">
                {event.organizerName && (
                  <div className="ed-contact-item">
                    <span className="ed-contact-icon" aria-hidden="true">
                      <i className="fa-solid fa-id-badge" />
                    </span>
                    <div>
                      <span className="ed-contact-label">Event Organizer</span>
                      <span className="ed-contact-value">{event.organizerName}</span>
                    </div>
                  </div>
                )}
                {event.organizerPhone && (
                  <a className="ed-contact-item" href={`tel:${event.organizerPhone}`}>
                    <span className="ed-contact-icon" aria-hidden="true">
                      <i className="fa-solid fa-phone" />
                    </span>
                    <div>
                      <span className="ed-contact-label">Phone</span>
                      <span className="ed-contact-value">{event.organizerPhone}</span>
                    </div>
                  </a>
                )}
                {event.organizerEmail && (
                  <a className="ed-contact-item" href={`mailto:${event.organizerEmail}`}>
                    <span className="ed-contact-icon" aria-hidden="true">
                      <i className="fa-solid fa-envelope" />
                    </span>
                    <div>
                      <span className="ed-contact-label">Email</span>
                      <span className="ed-contact-value">{event.organizerEmail}</span>
                    </div>
                  </a>
                )}
            </div>
          </div>
        </section>
      )}

      {/* ===== VIDEO ===== */}
      {videoId && (
        <section className="ed-section">
          <div className="ed-container">
            <div className="ed-section-head">
              <h2 className="ed-section-title">Watch</h2>
            </div>
            <div className="ed-video">
              <YoutubeVideoPlayer embedId={videoId} />
            </div>
          </div>
        </section>
      )}

      {/* ===== MORE UPCOMING EVENTS ===== */}
      {upcoming.length > 0 && (
        <div className="ed-more-band">
          <div className="ed-container">

            <div className="ed-section-head">
              <h2 className="ed-section-title">More upcoming events</h2>
              <Link className="ed-section-link" to="/dashboard/events">
                View all events <i className="fa-solid fa-arrow-right" />
              </Link>
            </div>

            <div className="ed-more-grid">
              {upcoming.map((item) => {
                const itemDate = toDate(item.eventDate);
                const itemImage = assetSrc(item.headerImage || item.images?.[0] || item.image);

                return (
                  <div className="ed-more-card" key={item.id}>
                    <div className="ed-more-media">
                      {itemImage && <img src={itemImage} alt={item.firstName} />}
                      {itemDate && (
                        <div className="ed-more-tag">
                          <strong>{MONTHS[itemDate.getMonth()]}</strong>
                          <span>{String(itemDate.getDate()).padStart(2, '0')}</span>
                        </div>
                      )}
                    </div>
                    <div className="ed-more-body">
                      <h4>{[item.firstName, item.lastName].filter(Boolean).join(' ')}</h4>
                      {itemDate && (
                        <div className="ed-more-line">
                          <i className="fa-solid fa-clock" />{formatTime(itemDate)}
                        </div>
                      )}
                      {item.location && (
                        <div className="ed-more-line">
                          <i className="fa-solid fa-location-dot" />{item.location}
                        </div>
                      )}
                      <div className="ed-more-foot">
                        <span className="ed-more-going">
                          <i className="fa-solid fa-user-group" />
                          {item.participantCount || 0} going
                        </span>
                        <Link className="ed-btn-view" to={`/dashboard/events/${item.id}`}>
                          View
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

          </div>
        </div>
      )}

      {/* ===== MEMBERSHIP CTA ===== */}
      <section className="ed-section" style={{ paddingTop: '52px' }}>
        <div className="ed-container">
          <div className="ed-cta">

            {ctaImage && (
              <div className="ed-cta-bg">
                <img src={ctaImage} alt="" />
              </div>
            )}

            <div className="ed-cta-copy">
              <h2>
                Don&apos;t just watch.
                <span className="ed-accent">Be part of it.</span>
              </h2>
            </div>

            <div className="ed-cta-side">
              <p>
                Upgrade your membership to unlock exclusive events, experiences and a
                community that shares your passion.
              </p>
              <Link className="ed-btn ed-btn-primary" to="/dashboard/my-subscription">
                Upgrade Membership <i className="fa-solid fa-arrow-right" />
              </Link>
            </div>

          </div>
        </div>
      </section>

      {/* ===== TERMS & CONDITIONS ===== */}
      {event.termsAndConditions && (
        <div className="ed-terms-band">
          <div className="ed-container">
            <div className="ed-terms-block">

              <div className="ed-terms-head">
                <i className="fa-solid fa-file-lines" />
                <h2>Terms &amp; Conditions</h2>
              </div>

              <CollapsibleCopy
                id="ed-terms-body"
                moreLabel="Read full terms"
                lessLabel="Show less"
              >
                <p>{event.termsAndConditions}</p>
              </CollapsibleCopy>

            </div>
          </div>
        </div>
      )}

      <footer className="ed-footer">© 2026 Taurian. All rights reserved.</footer>

    </div>
  );
};

export default EventDetailsStatic;
